<template>
    <component
        :is="presentationComponent"
        v-if="presentationComponent"
        :visible="visible"
        :renderer-component="rendererComponent"
        :renderer-options="resolvedProfile ? resolvedProfile.rendererOptions : {}"
        :item="activeItem"
        :draft="draft"
        :suggestions="suggestions"
        :review-context="resolvedReviewContext"
        :violations="violations"
        :workspace-service="workspaceService"
        :loading="loading || rendererInitializing"
        :saving="saving"
        :completing="completing"
        :completion-decision="completionDecision"
        :error="error"
        :show-save-action="showSaveAction"
        :save-action="saveAction"
        :save-action-allowed="saveActionAllowed"
        :show-decision-actions="showDecisionActions"
        :show-reject-action="showRejectAction"
        :show-complete-action="showCompleteAction"
        :completion-allowed="rendererCompletionValid"
        :completion-issues="rendererCompletionIssues"
        :navigation="navigationState"
        :navigation-options="resolvedNavigationOptions"
        :navigation-disabled="isDirty"
        @input="updateDraft"
        @save="saveActiveItem"
        @complete="completeProcess"
        @navigate="navigateToItem"
        @navigate-previous="navigatePrevious"
        @navigate-next="navigateNext"
        @renderer-initialization-change="setRendererInitializing"
        @renderer-completion-validity-change="setRendererCompletionValidity"
        @renderer-completion-issues-change="setRendererCompletionIssues"
        @close="$emit('close')"
    />
</template>

<script>
import { createDocumentComplianceDraft, serializeDocumentComplianceDraft } from './payload/document-compliance-payload'
import { resolveReviewProfile } from './profiles/review-profile-registry'
import { resolveReviewPresentation } from './presentations/presentation-registry'
import { resolveReviewRenderer } from './document-compliance/renderer-registry'
import { resolveSingleReviewItem } from './navigation/single-item-navigation'
import {
    findAdjacentReviewItem,
    findNextUnreviewedItem,
    findReviewItem,
    getReviewItems,
    resolveSequentialReviewItem,
} from './navigation/sequential-item-navigation'
import { createReviewWorkspaceService } from './services/create-review-workspace-service'
import { applyReviewSuggestions } from './suggestions/review-suggestion-registry'
import { resolveReviewProductLookup } from './product-lookup/review-product-lookup'
import { valueAtSymfonyPropertyPath } from './validation/symfony-property-path'

const clone = value => JSON.parse(JSON.stringify(value || {}))
const REVIEW_SUMMARY_NAVIGATION_ID = '__review-summary__'

export default {
    name: 'ReviewWorkspaceHost',
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        processId: {
            type: [String, Number],
            default: null,
        },
        itemId: {
            type: [String, Number],
            default: null,
        },
        profile: {
            type: String,
            required: true,
        },
        service: {
            type: Object,
            default: null,
        },
        reviewContext: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            process: null,
            activeItem: null,
            draft: null,
            persistedDraft: null,
            suggestions: [],
            loading: false,
            rendererInitializing: false,
            rendererCompletionValid: true,
            rendererCompletionIssues: [],
            saving: false,
            completing: false,
            completionDecision: null,
            automaticCompletionFailed: false,
            error: '',
            violations: [],
            legislations: [],
            loadRevision: 0,
        }
    },
    computed: {
        resolvedProfile() {
            return resolveReviewProfile(this.profile)
        },
        presentationComponent() {
            return this.resolvedProfile
                ? resolveReviewPresentation(this.resolvedProfile.presentation)
                : null
        },
        rendererComponent() {
            return this.resolvedProfile
                ? resolveReviewRenderer(this.resolvedProfile.renderer)
                : null
        },
        workspaceService() {
            return this.service || createReviewWorkspaceService()
        },
        resolvedReviewContext() {
            return Object.freeze({
                productLookup: resolveReviewProductLookup({
                    profile: this.resolvedProfile,
                    context: this.reviewContext,
                    process: this.process,
                }),
                legislations: Object.freeze(this.legislations.slice()),
            })
        },
        shouldLoadLegislations() {
            return !!(
                this.resolvedProfile
                && this.resolvedProfile.context
                && this.resolvedProfile.context.legislations === true
            )
        },
        isDirty() {
            return JSON.stringify(this.draft) !== JSON.stringify(this.persistedDraft)
        },
        processIsOpen() {
            return this.process && this.process.status !== 'completed'
        },
        allItemsReviewed() {
            const items = this.process && Array.isArray(this.process.items) ? this.process.items : []

            return items.length > 0 && items.every(item => item && item.status === 'reviewed')
        },
        showSaveAction() {
            return !!this.activeItem
                && this.processIsOpen
                && (this.activeItem.status !== 'reviewed' || this.isDirty)
        },
        showDecisionActions() {
            return this.showRejectAction || this.showCompleteAction
        },
        showRejectAction() {
            return this.canDecideProcess
                && this.allowedProcessDecisions.includes('reject')
        },
        showCompleteAction() {
            return this.canDecideProcess
                && this.allowedProcessDecisions.includes('continue')
        },
        canDecideProcess() {
            return this.resolvedProfile
                && this.resolvedProfile.processDecisions !== false
                && this.processIsOpen
                && this.allItemsReviewed
                && !this.isDirty
                && (
                    !this.isSequentialNavigation
                    || !this.activeItem
                    || (!this.showReviewSummary && this.processCompletionMode === 'manual')
                    || (this.shouldCompleteProcessOnSave && this.automaticCompletionFailed)
                )
        },
        isSequentialNavigation() {
            if (!this.resolvedProfile) {
                return false
            }

            return this.resolvedProfile.navigation === 'sequential'
                || (
                    this.resolvedProfile.navigation === 'adaptive'
                    && getReviewItems(this.process).length > 1
                )
        },
        saveAction() {
            if (!this.isSequentialNavigation) {
                return 'save'
            }

            if (this.shouldCompleteProcessOnSave && this.isFinalPendingItem) {
                return 'save-and-complete'
            }

            return findNextUnreviewedItem(this.process, this.activeItem && this.activeItem.id)
                ? 'save-and-next'
                : 'save'
        },
        processCompletionMode() {
            return this.resolvedProfile
                && this.resolvedProfile.processCompletionMode === 'on-save'
                ? 'on-save'
                : 'manual'
        },
        allowedProcessDecisions() {
            const defaultDecisions = this.resolvedProfile
                && Array.isArray(this.resolvedProfile.allowedProcessDecisions)
                ? this.resolvedProfile.allowedProcessDecisions
                : ['continue', 'reject']
            const decisions = this.isSequentialNavigation
                && Array.isArray(this.resolvedProfile.sequentialAllowedProcessDecisions)
                ? this.resolvedProfile.sequentialAllowedProcessDecisions
                : defaultDecisions

            return decisions.filter(decision => ['continue', 'reject'].includes(decision))
        },
        shouldCompleteProcessOnSave() {
            return this.processCompletionMode === 'on-save'
                && this.resolvedProfile
                && this.resolvedProfile.processDecisions !== false
                && this.allowedProcessDecisions.includes('continue')
        },
        isFinalPendingItem() {
            if (!this.isSequentialNavigation || !this.activeItem) {
                return false
            }

            const pendingItems = getReviewItems(this.process)
                .filter(item => item && item.status !== 'reviewed')

            return pendingItems.length === 1
                && String(pendingItems[0].id) === String(this.activeItem.id)
        },
        saveActionAllowed() {
            return !this.shouldCompleteProcessOnSave || this.rendererCompletionValid
        },
        resolvedNavigationOptions() {
            return this.resolvedProfile && this.resolvedProfile.navigationOptions
                ? this.resolvedProfile.navigationOptions
                : {}
        },
        showReviewSummary() {
            return this.resolvedNavigationOptions.showSummary !== false
        },
        navigationState() {
            if (!this.isSequentialNavigation) {
                return null
            }

            const items = getReviewItems(this.process)
            const activeIndex = items.findIndex(item => (
                item && this.activeItem && String(item.id) === String(this.activeItem.id)
            ))

            return {
                activeItemId: this.activeItem
                    ? this.activeItem.id
                    : REVIEW_SUMMARY_NAVIGATION_ID,
                summaryItemId: REVIEW_SUMMARY_NAVIGATION_ID,
                summaryAvailable: this.showReviewSummary && this.allItemsReviewed,
                current: activeIndex >= 0 ? activeIndex + 1 : 0,
                total: items.length,
                reviewed: items.filter(item => item && item.status === 'reviewed').length,
                complete: items.length > 0 && this.allItemsReviewed && !this.activeItem,
                canGoPrevious: activeIndex > 0 || (!this.activeItem && items.length > 0),
                canGoNext: activeIndex >= 0 && (
                    activeIndex < items.length - 1
                    || (this.showReviewSummary && this.allItemsReviewed)
                ),
                items: items.map((item, index) => ({
                    id: item.id,
                    position: index + 1,
                    label: this.itemLabel(item, index),
                    status: item.status || null,
                })),
            }
        },
        loadKey() {
            return this.visible
                ? JSON.stringify([this.processId, this.itemId, this.profile])
                : null
        },
    },
    watch: {
        loadKey: {
            immediate: true,
            handler(key) {
                if (key) {
                    this.loadProcess()
                }
            },
        },
    },
    beforeDestroy() {
        this.loadRevision += 1
    },
    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)

            return translation && translation !== key ? translation : fallback
        },
        loadProcess() {
            const revision = this.loadRevision + 1
            this.loadRevision = revision
            this.error = ''
            this.violations = []
            this.automaticCompletionFailed = false
            this.rendererInitializing = !!(
                this.resolvedProfile
                && this.resolvedProfile.rendererOptions
                && this.resolvedProfile.rendererOptions.initialization
                && this.resolvedProfile.rendererOptions.initialization.showOverlay
            )
            this.rendererCompletionValid = true
            this.rendererCompletionIssues = []

            if (!this.resolvedProfile) {
                this.resetReviewState()
                this.error = this.translate(
                    'Component(ReviewProcess):unsupported-profile',
                    'This review profile is not supported.',
                )
                return Promise.resolve()
            }

            if (!this.processId) {
                this.resetReviewState()
                this.error = this.translate(
                    'Component(ReviewProcess):process-not-found',
                    'The review process could not be loaded.',
                )
                return Promise.resolve()
            }

            this.loading = true
            return Promise.all([
                this.workspaceService.getProcess(this.processId),
                this.loadLegislations(),
            ])
                .then(([process, legislations]) => {
                    if (revision !== this.loadRevision) {
                        return
                    }

                    this.legislations = legislations
                    this.applyProcess(process)
                    this.$emit('process-loaded', process)
                })
                .catch(error => {
                    if (revision === this.loadRevision) {
                        this.rendererInitializing = false
                        this.error = this.getErrorMessage(error)
                    }
                })
                .finally(() => {
                    if (revision === this.loadRevision) {
                        this.loading = false
                        if (!this.activeItem || !this.draft) {
                            this.rendererInitializing = false
                        }
                    }
                })
        },
        applyProcess(process, preferredItemId = undefined) {
            this.process = process

            if (!process || process.reviewableType !== 'document_compliance') {
                this.activeItem = null
                this.draft = null
                this.persistedDraft = null
                this.suggestions = []
                this.error = this.translate(
                    'Component(ReviewProcess):unsupported-reviewable-type',
                    'This review type is not supported.',
                )
                return
            }

            const resolution = this.isSequentialNavigation
                ? resolveSequentialReviewItem(
                    process,
                    typeof preferredItemId === 'undefined' ? this.itemId : preferredItemId,
                )
                : resolveSingleReviewItem(process, this.itemId)
            if (!resolution.item) {
                if (resolution.complete && !this.showReviewSummary) {
                    const items = getReviewItems(process)
                    const lastItem = items[items.length - 1]
                    if (lastItem) {
                        this.activateItem(lastItem)
                        return
                    }
                }
                if (resolution.complete && this.shouldCompleteProcessOnSave) {
                    const items = getReviewItems(process)
                    const lastItem = items[items.length - 1]
                    if (lastItem) {
                        this.automaticCompletionFailed = true
                        this.activateItem(lastItem)
                        return
                    }
                }
                const errors = {
                    'item-not-found': 'The selected document is not part of this review process.',
                    'item-missing': 'This review process does not contain a document.',
                    'item-selection-required': 'Select a document before opening single-document review.',
                }

                this.activeItem = null
                this.draft = null
                this.persistedDraft = null
                this.suggestions = []
                this.rendererInitializing = false
                this.rendererCompletionValid = true
                this.rendererCompletionIssues = []
                this.error = resolution.complete ? '' : errors[resolution.error]
                return
            }

            this.activateItem(resolution.item)
        },
        activateItem(item) {
            const itemChanged = !this.activeItem
                || String(this.activeItem.id) !== String(item.id)

            this.activeItem = item
            if (itemChanged) {
                this.rendererInitializing = !!(
                    this.resolvedProfile
                    && this.resolvedProfile.rendererOptions
                    && this.resolvedProfile.rendererOptions.initialization
                    && this.resolvedProfile.rendererOptions.initialization.showOverlay
                )
                this.rendererCompletionValid = true
                this.rendererCompletionIssues = []
            } else {
                this.rendererInitializing = false
            }
            const backendDraft = createDocumentComplianceDraft(item)
            const suggestionResult = applyReviewSuggestions(
                this.resolvedProfile.suggestions,
                {
                    process: this.process,
                    item,
                    draft: backendDraft,
                },
            )

            this.draft = suggestionResult.draft
            this.persistedDraft = clone(backendDraft)
            this.suggestions = suggestionResult.suggestions
            this.error = ''
        },
        itemLabel(item, index) {
            const payload = item && (
                item.effectivePayload || item.reviewedPayload || item.rawPayload
            )
            const title = payload && payload.documentInformation
                ? payload.documentInformation.title
                : null
            const metadata = item && item.metadata ? item.metadata : {}

            return title
                || metadata.filename
                || metadata.fileName
                || `${this.translate('Component(VerificationDocuments):label', 'Document')} ${index + 1}`
        },
        resetReviewState() {
            this.process = null
            this.activeItem = null
            this.draft = null
            this.persistedDraft = null
            this.suggestions = []
            this.legislations = []
            this.rendererInitializing = false
            this.rendererCompletionValid = true
            this.rendererCompletionIssues = []
            this.automaticCompletionFailed = false
        },
        loadLegislations() {
            const hasProvidedLegislations = !!(
                this.reviewContext
                && Object.prototype.hasOwnProperty.call(this.reviewContext, 'legislations')
                && Array.isArray(this.reviewContext.legislations)
            )
            const provided = hasProvidedLegislations
                ? this.reviewContext.legislations
                : []
            if (hasProvidedLegislations || !this.shouldLoadLegislations) {
                return Promise.resolve(this.normalizeLegislations(provided))
            }
            if (typeof this.workspaceService.getLegislations !== 'function') {
                return Promise.resolve([])
            }

            return this.workspaceService.getLegislations()
                .then(legislations => this.normalizeLegislations(legislations))
        },
        normalizeLegislations(legislations) {
            return (Array.isArray(legislations) ? legislations : [])
                .filter(legislation => {
                    const id = Number(legislation && legislation.id)

                    return Number.isInteger(id)
                        && id > 0
                        && typeof legislation.key === 'string'
                        && legislation.key.trim()
                })
                .map(legislation => ({
                    ...legislation,
                    id: Number(legislation.id),
                    key: legislation.key.trim(),
                    title: legislation.title || legislation.name || legislation.key.trim(),
                }))
        },
        setRendererInitializing(value) {
            this.rendererInitializing = value === true
        },
        setRendererCompletionValidity(value) {
            this.rendererCompletionValid = value !== false
        },
        setRendererCompletionIssues(value) {
            this.rendererCompletionIssues = Array.isArray(value) ? clone(value) : []
        },
        updateDraft(value) {
            const nextDraft = clone(value)
            this.violations = this.violations.filter(violation => {
                const path = violation && violation.path
                if (!path) {
                    return true
                }

                return valueAtSymfonyPropertyPath(this.draft, path)
                    === valueAtSymfonyPropertyPath(nextDraft, path)
            })
            this.draft = nextDraft
        },
        saveActiveItem() {
            if (!this.activeItem || !this.saveActionAllowed || this.saving || this.completing) {
                return Promise.resolve()
            }

            const savedItemId = this.activeItem.id
            this.saving = true
            this.automaticCompletionFailed = false
            this.error = ''
            this.violations = []

            return this.workspaceService.updateItem(this.processId, this.activeItem.id, {
                status: 'reviewed',
                reviewedPayload: serializeDocumentComplianceDraft(this.draft),
            })
                .then(() => this.workspaceService.getProcess(this.processId))
                .then(process => {
                    const savedItem = findReviewItem(process, savedItemId)
                    const nextItem = this.isSequentialNavigation
                        ? findNextUnreviewedItem(process, savedItemId)
                        : null
                    const items = getReviewItems(process)
                    const allItemsReviewed = items.length > 0
                        && items.every(item => item && item.status === 'reviewed')
                    const shouldComplete = this.shouldCompleteProcessOnSave && allItemsReviewed
                    let preferredItemId
                    if (this.isSequentialNavigation) {
                        preferredItemId = nextItem
                            ? nextItem.id
                            : (shouldComplete || !this.showReviewSummary ? savedItemId : null)
                    }

                    this.applyProcess(process, preferredItemId)
                    this.$emit('item-saved', savedItem)

                    if (shouldComplete) {
                        return this.runProcessCompletion('continue', { automatic: true })
                    }

                    return undefined
                })
                .catch(error => {
                    const data = error && error.response ? error.response.data : null
                    if (error && error.response && error.response.status === 422 && data) {
                        this.violations = Array.isArray(data.violations) ? data.violations : []
                        this.error = this.violations.length ? '' : (data.message || '')
                        return
                    }

                    this.error = this.getErrorMessage(error)
                })
                .finally(() => {
                    this.saving = false
                })
        },
        navigateToItem(itemId) {
            if (!this.isSequentialNavigation || this.saving || this.completing) {
                return
            }
            if (this.isDirty) {
                this.error = this.translate(
                    'Component(ReviewProcess):save-before-navigation',
                    'Save your changes before opening another document.',
                )
                return
            }

            if (itemId === REVIEW_SUMMARY_NAVIGATION_ID
                && this.showReviewSummary
                && this.allItemsReviewed) {
                this.openReviewSummary()
                return
            }

            const item = findReviewItem(this.process, itemId)
            if (item) {
                this.activateItem(item)
            }
        },
        navigatePrevious() {
            const items = getReviewItems(this.process)
            const previous = this.activeItem
                ? findAdjacentReviewItem(this.process, this.activeItem.id, -1)
                : items[items.length - 1]

            if (previous) {
                this.navigateToItem(previous.id)
            }
        },
        navigateNext() {
            if (!this.activeItem) {
                return
            }
            const next = findAdjacentReviewItem(this.process, this.activeItem.id, 1)
            if (next) {
                this.navigateToItem(next.id)
            } else if (this.showReviewSummary && this.allItemsReviewed) {
                this.openReviewSummary()
            }
        },
        openReviewSummary() {
            this.activeItem = null
            this.draft = null
            this.persistedDraft = null
            this.suggestions = []
            this.violations = []
            this.rendererInitializing = false
            this.rendererCompletionValid = true
            this.rendererCompletionIssues = []
            this.error = ''
        },
        completeProcess(decision) {
            if (!this.canDecideProcess
                || !this.allowedProcessDecisions.includes(decision)
                || (decision === 'continue' && !this.rendererCompletionValid)
                || this.completing
                || this.saving) {
                return Promise.resolve()
            }

            return this.runProcessCompletion(decision)
        },
        runProcessCompletion(decision, { automatic = false } = {}) {
            this.completing = true
            this.completionDecision = decision
            this.error = ''

            return this.workspaceService.completeProcess(this.processId, { decision })
                .then(() => {
                    this.$emit('completed', {
                        process: this.process,
                        decision,
                    })
                    this.$emit('close')
                })
                .catch(error => {
                    if (automatic) {
                        this.automaticCompletionFailed = true
                    }
                    this.error = this.getErrorMessage(error)
                })
                .finally(() => {
                    this.completing = false
                    this.completionDecision = null
                })
        },
        getErrorMessage(error) {
            const data = error && error.response ? error.response.data : null

            return (data && (data.message || data.title || data.detail))
                || (error && error.message)
                || this.translate('Component(Error):default', 'Something went wrong.')
        },
    },
}
</script>
